import type { TowerType } from '../core/types';
import { type BagSlot, rollTable } from './restaurant';

/**
 * The Capybara Event (specs: 2026-09-15-capybara-event-design.md and
 * 2026-09-16-capy-towers-design.md).
 * Pure — no Phaser, no wall clock, no randomness of its own: the scene
 * injects `rand` (a seeded Rng in the e2e) and drives the run through CapyRun.
 */

// ── The capsule ──────────────────────────────────────────────

/** Capycoins for one capsule. */
export const CAPSULE_COST = 100;
export const CAPSULE_SHORT_TEXT = 'Not enough Capycoins';

/**
 * Real time a bought capsule spins for, in milliseconds (one minute).
 *
 * Wall-clock like the takeout bag's ten, stored as the purchase stamp and read
 * through `remainingMs`, so it keeps running while the game is closed.
 */
export const CAPSULE_PREP_MS = 60 * 1000;

/** Capycoins refunded when a capsule rolls a tower the player already owns. */
export const CAPSULE_DUPLICATE_REFUND = 75;

/**
 * What a capsule holds, rarest last. Weights sum to exactly 1 (the unit tests
 * assert it), and the order is also the display order — the odds list and the
 * roulette strip both read straight off this array.
 */
export const CAPSULE_ODDS: readonly BagSlot[] = [
  { tower: 'capy', weight: 0.4, rarity: 'Common' },
  { tower: 'magecapy', weight: 0.25, rarity: 'Uncommon' },
  { tower: 'hog', weight: 0.15, rarity: 'Rare' },
  { tower: 'minigunner', weight: 0.1, rarity: 'Epic' },
  { tower: 'generalcapy', weight: 0.05, rarity: 'Legendary' },
  { tower: 'galacticcapy', weight: 0.03, rarity: 'Mythical' },
  { tower: 'kingcapy', weight: 0.02, rarity: 'Ketchuptastic' },
];

/**
 * Every tower the capsule can contain, in display order.
 *
 * Also the list of towers the capsule is the ONLY source of: `config/unlocks`
 * keeps them out of the shop's shelves and `config/varietyOrb` subtracts them
 * from the orb's pool, so nothing else can hand one out.
 */
export const CAPSULE_TOWERS: readonly TowerType[] = CAPSULE_ODDS.map((s) => s.tower);

/** The slot a tower occupies in the capsule, or undefined if it can't drop it. */
export function capsuleSlotFor(tower: TowerType): BagSlot | undefined {
  return CAPSULE_ODDS.find((s) => s.tower === tower);
}

/**
 * Draw one tower from a capsule. `rand` must return a value in [0,1) and is
 * REQUIRED — the render layer passes the real generator, the tests pass a
 * fixed sequence. It has no default on purpose: `src/config` is part of the
 * pure layer, which the purity test forbids from naming a nondeterministic
 * source.
 */
export function rollCapsule(rand: () => number): TowerType {
  return rollTable(CAPSULE_ODDS, rand);
}

// ── The minigame ───────────────────────────────────────────

export const CAPY_TOTAL = 100;
export const CAPY_WAVE_SIZE = 4;
export const CAPY_WAVE_MS = 2000;
export const CAPY_RED_CHANCE = 0.1;
/** Pixels per second, top (y = -60) to bottom (y = 780) in ~3.5 s — "medium". */
export const CAPY_SPEED = 240;
export const CAPY_SHAKE_MS = 1000;
export const CAPY_COUNTDOWN_STEP_MS = 1000;
/** The Bladesman's notice auto-dismisses after this (a tap dismisses sooner). */
export const CAPY_NOTICE_MS = 5000;
export const BLADESMAN_ART = 'tower-blade-3';
export const BLADESMAN_NAME = 'Cheese Bladesman';
export const BLADESMAN_LINE =
  'The Capybaras are one of our greatest allies, we need to save them from the imposters lurking between them!';

export type CapyKind = 'capy' | 'red';

/** `total` kinds, one rand draw each in order: below CAPY_RED_CHANCE is red. */
export function rollCapys(rand: () => number, total = CAPY_TOTAL): CapyKind[] {
  const out: CapyKind[] = [];
  for (let i = 0; i < total; i++) out.push(rand() < CAPY_RED_CHANCE ? 'red' : 'capy');
  return out;
}

/** Split a roll into waves of `size` (the last may be short). */
export function wavesOf(kinds: readonly CapyKind[], size = CAPY_WAVE_SIZE): CapyKind[][] {
  const waves: CapyKind[][] = [];
  for (let i = 0; i < kinds.length; i += size) waves.push(kinds.slice(i, i + size));
  return waves;
}

export interface CapySpawn {
  index: number;
  kind: CapyKind;
}

/**
 * One run's state: which capybaras have spawned, which are resolved (tapped
 * or gone off the bottom), and the score — floored at 0, +1 per capybara,
 * -1 per red. Indices are global (0..total-1), in spawn order.
 */
export class CapyRun {
  private readonly kinds: readonly CapyKind[];
  private readonly waveSize: number;
  private _spawned = 0;
  private _score = 0;
  private readonly resolved = new Set<number>();

  constructor(kinds: readonly CapyKind[], waveSize = CAPY_WAVE_SIZE) {
    this.kinds = kinds;
    this.waveSize = Math.max(1, Math.floor(waveSize));
  }

  get total(): number {
    return this.kinds.length;
  }

  get spawned(): number {
    return this._spawned;
  }

  get score(): number {
    return this._score;
  }

  kindOf(index: number): CapyKind {
    return this.kinds[index];
  }

  hasNextWave(): boolean {
    return this._spawned < this.kinds.length;
  }

  /** The next wave's spawns (marks them spawned); empty once the roll is out. */
  nextWave(): CapySpawn[] {
    const wave: CapySpawn[] = [];
    const end = Math.min(this.kinds.length, this._spawned + this.waveSize);
    for (let i = this._spawned; i < end; i++) wave.push({ index: i, kind: this.kinds[i] });
    this._spawned = end;
    return wave;
  }

  /** A tap: the kind scored, or null when not spawned / already resolved. */
  hit(index: number): CapyKind | null {
    if (index < 0 || index >= this._spawned || this.resolved.has(index)) return null;
    this.resolved.add(index);
    const kind = this.kinds[index];
    if (kind === 'red') this._score = Math.max(0, this._score - 1);
    else this._score += 1;
    return kind;
  }

  /** Ran off the bottom: resolved, no score change. Idempotent. */
  miss(index: number): void {
    if (index < 0 || index >= this._spawned) return;
    this.resolved.add(index);
  }

  /** Every capybara spawned AND resolved. */
  finished(): boolean {
    return this._spawned === this.kinds.length && this.resolved.size === this.kinds.length;
  }
}
